/* CONTEXT:START
Wave: 3
Status: implemented
Dependencies: [API Gateway, Cognito authentication, Lambda functions]
Risk-Level: medium
Validation-Method: integration-test
Performance-Target: <2s
Last-Updated: 2025-08-05
CONTEXT:END */

import { Submission, WorkflowState } from '../types';

/**
 * Real API service for workflow operations
 * Handles submission workflow state management and version control
 */
export class WorkflowApiService {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://x2n6b1kv66.execute-api.us-east-1.amazonaws.com/dev';
  }

  /**
   * Set authentication token from Cognito
   */
  setAuthToken(token: string) {
    this.authToken = token;
  }

  /**
   * Update submission workflow state with audit trail
   */
  async updateWorkflowState(
    submissionId: string,
    newState: WorkflowState,
    feedback?: string,
    penholderUserId?: string
  ): Promise<Submission> {
    try {
      const payload = {
        workflowState: newState,
        penholderFeedback: feedback || '',
        penholderUserId: penholderUserId
      };

      const response = await this.makeRequest(`/api/submissions/${submissionId}/workflow`, 'PUT', payload);
      
      if (response.success && response.data) {
        return this.mapSubmissionResponse(response.data);
      }
      
      throw new Error('Invalid response from workflow update');
    } catch (error) {
      console.error('Error updating workflow state:', error);
      throw new Error('Failed to update submission workflow state');
    }
  }

  /**
   * Create new submission version
   */
  async createSubmissionVersion(
    submissionId: string,
    newContent: string,
    editedBy: string,
    editReason?: string
  ): Promise<any> {
    try {
      const payload = {
        content: newContent,
        editedBy,
        editReason: editReason || 'Penholder edit'
      };

      const response = await this.makeRequest(`/api/submissions/${submissionId}/versions`, 'POST', payload);
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error('Invalid response from version creation');
    } catch (error) {
      console.error('Error creating submission version:', error);
      throw new Error('Failed to create submission version');
    }
  }

  /**
   * Get submission version history
   */
  async getVersionHistory(submissionId: string): Promise<any[]> {
    try {
      const response = await this.makeRequest(`/api/submissions/${submissionId}/versions`, 'GET');
      
      if (response.success && response.data && response.data.versions) {
        return response.data.versions;
      }
      
      return [];
    } catch (error) {
      console.error('Error getting version history:', error);
      throw new Error('Failed to get submission version history');
    }
  }

  /**
   * Get submission audit trail
   */
  async getAuditTrail(submissionId: string): Promise<any[]> {
    try {
      const response = await this.makeRequest(`/api/submissions/${submissionId}/audit`, 'GET');
      
      if (response.success && response.data && response.data.auditTrail) {
        return response.data.auditTrail;
      }
      
      return [];
    } catch (error) {
      console.error('Error getting audit trail:', error);
      throw new Error('Failed to get submission audit trail');
    }
  }

  /**
   * Create submission with workflow (for new submissions)
   */
  async createSubmissionWithWorkflow(
    userId: string,
    content: string,
    type: 'highlight' | 'lowlight'
  ): Promise<Submission> {
    try {
      const payload = {
        userId,
        content,
        type,
        workflowState: 'submitted' as WorkflowState
      };

      const response = await this.makeRequest('/api/submissions/workflow', 'POST', payload);
      
      if (response.success && response.data) {
        return this.mapSubmissionResponse(response.data);
      }
      
      throw new Error('Invalid response from submission creation');
    } catch (error) {
      console.error('Error creating submission with workflow:', error);
      throw new Error('Failed to create submission');
    }
  }

  /**
   * Update submission with versioning
   */
  async updateSubmissionWithVersioning(
    submissionId: string,
    newContent: string,
    editedBy: string,
    editReason?: string
  ): Promise<Submission> {
    try {
      const payload = {
        content: newContent,
        editedBy,
        editReason: editReason || 'Content update'
      };

      const response = await this.makeRequest(`/api/submissions/${submissionId}/versioned`, 'PUT', payload);
      
      if (response.success && response.data) {
        return this.mapSubmissionResponse(response.data);
      }
      
      throw new Error('Invalid response from submission update');
    } catch (error) {
      console.error('Error updating submission with versioning:', error);
      throw new Error('Failed to update submission');
    }
  }

  /**
   * Map API response to frontend Submission interface
   */
  private mapSubmissionResponse(apiSubmission: any): Submission {
    return {
      id: apiSubmission.id,
      userId: apiSubmission.userId,
      content: apiSubmission.content,
      type: apiSubmission.type as 'highlight' | 'lowlight',
      month: apiSubmission.month || new Date().getMonth() + 1,
      year: apiSubmission.year || new Date().getFullYear(),
      createdAt: new Date(apiSubmission.createdAt),
      updatedAt: new Date(apiSubmission.updatedAt),
      workflowState: apiSubmission.workflowState as WorkflowState,
      
      // Version control fields
      currentVersion: apiSubmission.currentVersion || '1.0',
      originalVersion: apiSubmission.originalVersion || '1.0',
      addedToReport: apiSubmission.addedToReport || false,
      addedToReportAt: apiSubmission.addedToReportAt,
      addedToReportBy: apiSubmission.addedToReportBy,
      reportInclusionReason: apiSubmission.reportInclusionReason,
      reportExclusionReason: apiSubmission.reportExclusionReason,
      isIncludedInReport: apiSubmission.isIncludedInReport || false,
      
      // Penholder-specific fields
      penholderAssignedTo: apiSubmission.assignedToPenholder,
      penholderAssignedAt: apiSubmission.penholderAssignedAt,
      penholderReviewDeadline: apiSubmission.penholderReviewDeadline,
      penholderFeedback: apiSubmission.penholderFeedback,
      penholderEditedAt: apiSubmission.penholderEditedAt
    };
  }

  /**
   * Make authenticated HTTP request to API Gateway
   */
  private async makeRequest(endpoint: string, method: string, body?: any): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authentication header if token is available
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const config: RequestInit = {
      method,
      headers,
      mode: 'cors',
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error && errorJson.error.message) {
            errorMessage = errorJson.error.message;
          }
        } catch {
          // Use default error message if JSON parsing fails
        }
        
        throw new Error(`${response.status} - ${errorMessage}`);
      }

      const responseText = await response.text();
      
      // Handle empty responses
      if (!responseText) {
        return { success: true };
      }

      return JSON.parse(responseText);
    } catch (error) {
      if (error instanceof Error) {
        console.error(`API request failed: ${method} ${url}`, error.message);
        throw error;
      }
      
      throw new Error(`Network error: ${method} ${url}`);
    }
  }
}

// Export singleton instance
export const workflowApiService = new WorkflowApiService();
